import { useMemo } from 'react';
import { useCurrentOrganisation } from '#pie/auth';
import {
  type KnowledgeArticleListDto,
  type KnowledgeArticleListDtoListResponse,
  useGetKnowledgeArticleCategories,
} from '#pie/internal/api/pie-bff';
import { ErrorMessageBlock } from '../error-message/ErrorMessageBlock';
import { SkeletonLines } from '../skeleton-lines/SkeletonLines';
import { KnowledgeTabArticles } from './KnowledgeTabArticles';

interface KnowledgeTabSelectedlProps {
  data: KnowledgeArticleListDtoListResponse | undefined;
  isLoading: boolean;
}

export const KnowledgeTabSelected = ({
  data,
  isLoading,
}: KnowledgeTabSelectedlProps) => {
  const {
    data: categories,
    error,
    isLoading: isLoadingCategories,
  } = useGetKnowledgeArticleCategories();

  const { currentAccount } = useCurrentOrganisation();
  const marketRole = currentAccount?.marketRole;

  const groupedArticles = useMemo(() => {
    if (!marketRole || !categories?.items) {
      return {};
    }
    return (
      data?.items.reduce(
        (acc, article) => {
          if (
            !categories?.items.some(
              (c) =>
                c.id === article.mainCategory.id &&
                c.marketRoles.includes(marketRole!),
            )
          ) {
            return acc;
          }
          if (acc[article.mainCategory.label]) {
            acc[article.mainCategory.label].push(article);
          } else acc[article.mainCategory.label] = [article];
          return acc;
        },
        {} as Record<string, KnowledgeArticleListDto[]>,
      ) ?? {}
    );
  }, [data?.items, marketRole, categories?.items]);

  if (error) {
    return <ErrorMessageBlock error={error} />;
  }

  return (
    <>
      {isLoading || isLoadingCategories ? (
        <SkeletonLines lines={2} />
      ) : (
        <KnowledgeTabArticles
          groupedArticles={groupedArticles}
          isLoading={isLoading}
        />
      )}
    </>
  );
};
